import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus, Eye, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { canWrite } from '../../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Suppliers = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    registration_number: '',
    email: '',
    phone: '',
    address: ''
  });

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`, { headers: getAuthHeader() });
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to fetch suppliers');
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/suppliers`, formData, { headers: getAuthHeader() });
      toast.success('Supplier added successfully');
      setIsOpen(false);
      fetchSuppliers();
      setFormData({ name: '', registration_number: '', email: '', phone: '', address: '' });
    } catch (error) {
      toast.error('Failed to add supplier');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (name) => {
    if (user.role !== 'admin') {
      toast.error('Only admin can delete');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;
    try {
      await axios.delete(`${API}/suppliers/${name}`, { headers: getAuthHeader() });
      toast.success('Supplier deleted');
      fetchSuppliers();
    } catch (error) {
      toast.error('Failed to delete supplier');
    }
  };

  return (
    <Card className="p-6 border border-border">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('suppliers')}</h2>
        {canWrite(user?.role) && <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-accent hover:bg-accent/90" data-testid="add-supplier-btn">
              <Plus className="w-4 h-4" />
              <span lang={language === 'ar' ? 'ar' : 'en'}>{t('add')}</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>{t('addSupplier')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('name')}</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="supplier-name-input" />
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('registrationNumber')}</Label>
                <Input value={formData.registration_number} onChange={(e) => setFormData({...formData, registration_number: e.target.value})} data-testid="supplier-registration-input" />
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('email')}</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="supplier-email-input" />
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('phone')}</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="supplier-phone-input" />
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('address')}</Label>
                <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} data-testid="supplier-address-input" />
              </div>
              <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="supplier-submit-btn" disabled={isSubmitting}>
                <span lang={language === 'ar' ? 'ar' : 'en'}>{t('save')}</span>
              </Button>
            </form>
          </DialogContent>
        </Dialog>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {suppliers.map((supplier, idx) => (
          <Card key={idx} className="p-4 border border-border hover:border-slate-300 transition-colors" data-testid={`supplier-card-${idx}`}>
            <div className="space-y-2">
              <h3 className="font-semibold text-lg">{supplier.name}</h3>
              <p className="text-sm text-muted-foreground">{supplier.registration_number}</p>
              <p className="text-sm text-muted-foreground">{supplier.email}</p>
              <p className="text-sm text-muted-foreground">{supplier.phone}</p>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => navigate(`/finance/supplier/${supplier.id}`)} data-testid={`view-supplier-${idx}`}>
                  <Eye className="w-4 h-4 me-2" />
                  <span lang={language === 'ar' ? 'ar' : 'en'}>{t('view')}</span>
                </Button>
                {user?.role === 'admin' && (
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(supplier.name)} data-testid={`delete-supplier-${idx}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
      {suppliers.length === 0 && (
        <div className="text-center py-12 text-muted-foreground" data-testid="empty-suppliers">
          <p lang={language === 'ar' ? 'ar' : 'en'}>{t('noSuppliersYet')}</p>
        </div>
      )}
    </Card>
  );
};